import type { VirtualWindow } from './types';

/** Fallback when `bodyRowHeight` is `fit-content` / unparseable. ~NxTable default. */
const ROW_HEIGHT_FALLBACK_PX = 46;

/**
 * Best-effort px for the CSS `bodyRowHeight` token so a windowed list can
 * size the scrollbar to `count * row` before any row is measured.
 * `fit-content` / empty → fallback (never 0 — that collapsed the scroller
 * to the first page and "scroll to end" landed at ~row 100 of 150).
 * `emPx` is the font size an `em` resolves against — the table's, when it is
 * known; 16px (the root default) only as a last resort.
 */
export function parseRowHeightPx(
  css: string,
  fallback = ROW_HEIGHT_FALLBACK_PX,
  emPx = 16
): number {
  const t = (css ?? '').trim();
  if (!t || t === 'fit-content' || t === 'auto') return fallback;
  const px = /^([\d.]+)px$/i.exec(t);
  if (px) return Number(px[1]);
  const em = /^([\d.]+)em$/i.exec(t);
  if (em) return Number(em[1]) * emPx;
  const calc = /^calc\(\s*([\d.]+)em\s*\/\s*([\d.]+)\s*\)$/i.exec(t);
  if (calc) {
    const den = Number(calc[2]);
    if (den > 0) return (Number(calc[1]) * emPx) / den;
  }
  return fallback;
}

/** Prefix sum of row heights. Length is count + 1; the last entry is the
 *  total height. heightOf(i) is the (measured or estimated) height of row i. */
export function buildOffsets(count: number, heightOf: (i: number) => number): number[] {
  const offsets = new Array<number>(count + 1);
  offsets[0] = 0;
  for (let i = 0; i < count; i++) {
    offsets[i + 1] = offsets[i] + heightOf(i);
  }
  return offsets;
}

/** Largest index i with offsets[i] <= y, clamped to [0, count-1].
 *  Binary search over the prefix-sum array. */
export function indexAtOffset(offsets: number[], y: number): number {
  const count = offsets.length - 1;
  if (count <= 0) return 0;
  if (y <= 0) return 0;
  if (y >= offsets[count]) return count - 1;
  let lo = 0;
  let hi = count; // search in [0, count]
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (offsets[mid] <= y) lo = mid + 1;
    else hi = mid;
  }
  return lo - 1;
}

/** Resolve the render window from the prefix-sum offsets and scroll state. */
export function computeWindow(args: {
  offsets: number[];
  scrollTop: number;
  containerHeight: number;
  overscan: number;
  dragged: number | null;
}): VirtualWindow {
  const { offsets, scrollTop, containerHeight, overscan, dragged } = args;
  const count = offsets.length - 1;
  if (count <= 0) {
    return { start: 0, end: 0, topPad: 0, bottomPad: 0 };
  }
  const firstVisible = indexAtOffset(offsets, scrollTop);
  const lastVisible = indexAtOffset(offsets, scrollTop + containerHeight);
  let start = Math.max(0, firstVisible - overscan);
  let end = Math.min(count, lastVisible + 1 + overscan);

  if (dragged !== null && dragged >= 0 && dragged < count) {
    if (dragged < start) start = dragged;
    if (dragged >= end) end = dragged + 1;
  }

  return {
    start,
    end,
    topPad: offsets[start],
    bottomPad: offsets[count] - offsets[end],
  };
}
